/**
 * Removes all keys with `null` values from the provided object. If the value of a key
 * is an object, the method is applied recursively to clean nested objects. If a nested
 * object becomes empty after removing `null` values, it itself is removed.
 *
 * @param {T} obj - The object to be cleaned of keys with `null` values.
 * @return A new object with all `null` keys removed, with `null` stripped from value types.
 */
type NonNullableValues<T> = { [K in keyof T]: NonNullable<T[K]> };

export function removeNullKeys<T extends object>(obj: T): Partial<NonNullableValues<T>> {
  const result = { ...obj };

  Object.keys(result).forEach(key => {
    const value = result[key as keyof T];

    // Remove nulls and empty strings (including whitespace-only)
    if (value === null) {
      delete result[key as keyof T];
    } else {
      if (value && typeof value === 'object') {
        result[key as keyof T] = removeNullKeys(value as object) as any;

        // Remove the key if the nested object becomes empty after cleaning
        if (Object.keys(result[key as keyof T] as object).length === 0) {
          delete result[key as keyof T];
        }
      }
    }
  });

  return result;
}
